import { useTheme } from "@deere/fuel-react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Link as FuelLink,
  LinkProps as FuelLinkProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { FC } from "react";
import {
  Link as ReactRouterLink,
  LinkProps as ReactRouterLinkProps,
} from "react-router-dom";
import withVerticalCenter from "../hoc/withVerticalCenter/withVerticalCenter";

export interface LinkProp {
  openInNewTab?: boolean;
  StartIcon?: JSX.Element;
  Loader?: JSX.Element;
  native?: boolean;
  tooltip?: string;
  generateAttachmentURL?: () => Promise<string>;
}

export type CommonLinkProps = LinkProp & ReactRouterLinkProps & FuelLinkProps;

const Link: FC<CommonLinkProps> = ({
  to,
  openInNewTab,
  StartIcon,
  Loader,
  children,
  native = false,
  tooltip,
  ...rest
}: CommonLinkProps) => {
  const theme = useTheme();

  const tooltipText = tooltip ?? children;

  const commonProps = {
    style: {
      color: theme.tokens.FuelColorTextBrandPrimary,
      display: "flex",
      gap: theme.tokens.FuelSpacing1,
    },
    ...(openInNewTab
      ? {
          target: "_blank",
          rel: "noreferrer",
        }
      : {}),
    ...rest,
  };

  const linkChildren = (
    <>
      <Box>{withVerticalCenter(<Typography>{children}</Typography>)}</Box>
      <Box>
        {openInNewTab &&
          withVerticalCenter(
            <OpenInNewIcon
              fontSize="small"
              sx={{
                color: theme.tokens.FuelColorTextBrandPrimary,
              }}
            />
          )}
        {Loader || null}
      </Box>
    </>
  );

  return (
    <Box sx={{ display: "flex", gap: theme.tokens.FuelSpacing1 }}>
      {StartIcon && <Box>{withVerticalCenter(StartIcon)}</Box>}
      <Tooltip title={tooltipText}>
        {native ? (
          <FuelLink
            data-testid="fuel-link"
            href={to as string}
            {...commonProps}
          >
            {linkChildren}
          </FuelLink>
        ) : (
          <ReactRouterLink to={to} {...commonProps}>
            {linkChildren}
          </ReactRouterLink>
        )}
      </Tooltip>
    </Box>
  );
};

export default Link;
